import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLoaderData, useNavigate, useRevalidator, type LoaderFunctionArgs } from 'react-router-dom';
import { Button, Label, Modal, Textarea, cn } from '@immo/ui-kit';
import { DocumentCategorySchema, type DocumentAnalysisSkillView, type DocumentCategory } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { BreadcrumbLeading } from '@/components/layout/page-leading';
import { TagIcon } from '@/components/layout/icons';

export async function documentAnalysisSkillEditorLoader({
  params,
}: LoaderFunctionArgs): Promise<DocumentAnalysisSkillView> {
  const category = DocumentCategorySchema.safeParse(params.category);

  if (!category.success) {
    throw new Response('Unknown document category', { status: 404 });
  }

  const result = await apiClient.adminDocumentAnalysisSkills.get({ pathParams: { category: category.data } });

  if (!result.ok) {
    throw new Response('Failed to load skill', { status: result.status });
  }

  return result.body;
}

function errorCodeOf(body: unknown): string | undefined {
  return (body as { error?: { code?: string } } | undefined)?.error?.code;
}

export function DocumentAnalysisSkillEditorPage() {
  const t = useTranslations('pages.admin.skills.document-analyses.editor');
  const tCategory = useTranslations('pages.admin.skills.document-analyses');
  const intl = useIntl();
  const view = useLoaderData() as DocumentAnalysisSkillView;
  const navigate = useNavigate();
  const revalidator = useRevalidator();

  const [prompt, setPrompt] = useState(view.prompt ?? view.effectiveDefault);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [staleOpen, setStaleOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);

  const category = view.category as DocumentCategory;
  const isUnconfigured = view.status === 'unconfigured';
  // The baseline (the system fallback prompt) is the starting point for both an
  // unconfigured AND an inactive type skill, so the marker shows in both states
  // (AC "unconfigured OR inactive type skill → baseline marker").
  const showsBaseline = view.status === 'unconfigured' || view.status === 'inactive';

  // Re-sync the field whenever the server view changes (after a save, a status
  // flip, or a stale-edit reload) so the editor reflects the persisted state.
  useEffect(() => {
    setPrompt(view.prompt ?? view.effectiveDefault);
    setInlineError(null);
  }, [view.prompt, view.effectiveDefault, view.status, view.version]);

  const goToList = () => navigate('/admin/skills/document-analyses');

  const handleResponse = (result: { ok: boolean; status: number; body: unknown }): boolean => {
    if (result.ok) {
      setSaved(true);
      revalidator.revalidate();

      return true;
    }

    if (result.status === 409) {
      setStaleOpen(true);

      return false;
    }

    const code = errorCodeOf(result.body);

    if (code === 'admin.skill.prompt-empty') setInlineError(t('errors.prompt-empty'));
    else if (code === 'admin.skill.prompt-too-long') setInlineError(t('errors.prompt-too-long'));
    else setInlineError(t('errors.generic'));

    return false;
  };

  const save = async () => {
    setInlineError(null);
    setSaved(false);

    if (prompt.trim().length === 0) {
      setInlineError(t('errors.prompt-empty'));

      return;
    }

    setBusy(true);

    try {
      const result = await apiClient.adminDocumentAnalysisSkills.save({
        pathParams: { category },
        body: { prompt, expectedVersion: view.version },
      });
      handleResponse(result);
    } finally {
      setBusy(false);
    }
  };

  const activate = async () => {
    if (view.version === null) return;
    setBusy(true);

    try {
      handleResponse(
        await apiClient.adminDocumentAnalysisSkills.activate({
          pathParams: { category },
          body: { expectedVersion: view.version },
        }),
      );
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async () => {
    if (view.version === null) return;
    setBusy(true);

    try {
      const ok = handleResponse(
        await apiClient.adminDocumentAnalysisSkills.deactivate({
          pathParams: { category },
          body: { expectedVersion: view.version },
        }),
      );

      if (ok) setDeactivateOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const reloadAfterStale = () => {
    setStaleOpen(false);
    revalidator.revalidate();
  };

  const attribution =
    view.lastEditedByName !== null && view.lastEditedAt !== null
      ? t('last-edited-by', {
          name: view.lastEditedByName,
          date: intl.formatDate(view.lastEditedAt, { dateStyle: 'short' }),
        })
      : t('never-edited');

  return (
    <>
      <PageTopbar
        leading={
          <BreadcrumbLeading
            icon={TagIcon}
            crumbs={[
              { label: tCategory('title'), to: '/admin/skills/document-analyses' },
              { label: tCategory(`category.${category}`) },
            ]}
          />
        }
      />
      <div className="mx-auto max-w-3xl space-y-4 p-6">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold text-text-primary">{tCategory(`category.${category}`)}</h1>
          <StatusBadge status={view.status} label={t(`status.${view.status}`)} />
        </div>
        <p className="text-sm text-text-tertiary">{attribution}</p>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label htmlFor="skill-prompt">{t('prompt-label')}</Label>
            {showsBaseline && <span className="text-xs text-text-tertiary">{t('baseline-marker')}</span>}
          </div>
          {isUnconfigured && <p className="text-xs text-text-tertiary">{t('baseline-hint')}</p>}
          <Textarea
            id="skill-prompt"
            value={prompt}
            onChange={(event) => {
              setPrompt(event.target.value);
              setInlineError(null);
              setSaved(false);
            }}
            disabled={busy}
            rows={16}
            // A prompt is code-like text: mono and denser than a form field.
            className="bg-canvas font-mono text-sm"
            invalid={inlineError !== null}
            aria-describedby={inlineError !== null ? 'skill-prompt-error' : undefined}
          />
          {inlineError !== null && (
            <p id="skill-prompt-error" className="text-sm text-danger-600">
              {inlineError}
            </p>
          )}
          {saved && <p className="text-sm text-success-700">{t('saved')}</p>}
        </div>

        <div className="flex items-center gap-2">
          <Button type="button" onClick={save} disabled={busy}>
            {isUnconfigured ? t('activate') : t('save')}
          </Button>
          {view.status === 'inactive' && (
            <Button type="button" variant="secondary" onClick={activate} disabled={busy}>
              {t('activate')}
            </Button>
          )}
          {view.status === 'active' && (
            <Button type="button" variant="ghost" onClick={() => setDeactivateOpen(true)} disabled={busy}>
              {t('deactivate')}
            </Button>
          )}
          <Button type="button" variant="ghost" onClick={goToList} disabled={busy}>
            {t('cancel')}
          </Button>
        </div>
      </div>

      <Modal
        open={staleOpen}
        onClose={() => setStaleOpen(false)}
        size="sm"
        title={t('stale.title')}
        footer={
          <Button type="button" onClick={reloadAfterStale}>
            {t('stale.reload')}
          </Button>
        }
      >
        <p className="text-sm text-text-secondary">{t('stale.body')}</p>
      </Modal>

      <Modal
        open={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
        size="sm"
        title={t('deactivate-confirm.title')}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setDeactivateOpen(false)} disabled={busy}>
              {t('deactivate-confirm.cancel')}
            </Button>
            <Button type="button" variant="danger" onClick={deactivate} disabled={busy}>
              {t('deactivate-confirm.confirm')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">{t('deactivate-confirm.body')}</p>
      </Modal>
    </>
  );
}

function StatusBadge({
  status,
  label,
}: {
  readonly status: DocumentAnalysisSkillView['status'];
  readonly label: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        status === 'active' ? 'bg-success-50 text-success-700' : 'bg-subtle text-text-tertiary',
      )}
    >
      {label}
    </span>
  );
}
